import React, { useEffect, useState } from 'react';

interface EstimateData {
  ProjectId: string;
  ProjectTitle: string;
  EstimatedHours: number;
  ActualHours: number;
  Variance: number;
  VariancePercent: number;
}

interface Props {
  teamId?: string;
  userId?: string;
  title?: string;
}

export default function ForecastVsActual({
  teamId,
  userId,
  title = 'Forecast vs Actuals'
}: Props) {
  const [data, setData] = useState<EstimateData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [totals, setTotals] = useState({ estimated: 0, actual: 0, variance: 0 });

  useEffect(() => {
    fetchData();
  }, [teamId, userId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      let url = '/estimates/forecast';
      if (teamId) {
        url += `/${teamId}`;
      }

      const estimatesRes = await fetch(url);
      const estimatesData = await estimatesRes.json();

      if (!estimatesData.success || !estimatesData.data) {
        setData([]);
        setTotals({ estimated: 0, actual: 0, variance: 0 });
        return;
      }

      const estimates = estimatesData.data;

      // Fetch actual hours from timesheets
      let timesheetUrl = '/timesheets';
      if (teamId) {
        timesheetUrl += `?teamId=${teamId}`;
      } else if (userId) {
        timesheetUrl += `?userId=${userId}`;
      }

      const timesheetsRes = await fetch(timesheetUrl);
      const timesheetsData = await timesheetsRes.json();
      const timesheets = timesheetsData.data || [];

      // Aggregate actual hours by project
      const actualByProject: { [key: string]: number } = {};
      timesheets.forEach((ts: any) => {
        if (ts.ProjectId) {
          actualByProject[ts.ProjectId] = (actualByProject[ts.ProjectId] || 0) + (ts.TotalHours || 0);
        }
      });

      // Build comparison data
      const comparisonData: EstimateData[] = estimates.map((est: any) => {
        const actual = actualByProject[est.ProjectId] || 0;
        const variance = est.EstimatedHours - actual;
        const variancePercent = est.EstimatedHours > 0
          ? ((variance / est.EstimatedHours) * 100)
          : 0;

        return {
          ProjectId: est.ProjectId,
          ProjectTitle: est.ProjectTitle || est.ProjectId,
          EstimatedHours: est.EstimatedHours,
          ActualHours: actual,
          Variance: variance,
          VariancePercent: variancePercent,
        };
      });

      // Calculate totals
      const totalEstimated = comparisonData.reduce((sum, item) => sum + item.EstimatedHours, 0);
      const totalActual = comparisonData.reduce((sum, item) => sum + item.ActualHours, 0);
      const totalVariance = totalEstimated - totalActual;

      setData(comparisonData);
      setTotals({
        estimated: totalEstimated,
        actual: totalActual,
        variance: totalVariance,
      });
    } catch (err: any) {
      console.error('Failed to fetch forecast vs actual data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getVarianceColor = (variance: number, isPercent: boolean = false) => {
    if (isPercent) {
      if (variance > 10) return '#dc3545'; // Red - over budget
      if (variance > 0) return '#ffc107'; // Yellow - slightly over
      if (variance < -10) return '#28a745'; // Green - under budget
      return '#20c997'; // Teal - slightly under
    } else {
      if (variance > 5) return '#dc3545';
      if (variance > 0) return '#ffc107';
      return '#28a745';
    }
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Loading forecast data...</div>;
  }

  if (error) {
    return (
      <div style={{
        padding: '1.5rem',
        background: '#f8d7da',
        color: '#721c24',
        borderRadius: '4px',
        border: '1px solid #f5c6cb',
      }}>
        Error: {error}
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem' }}>
      <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.1rem', color: '#333' }}>
        {title}
      </h3>

      {data.length === 0 ? (
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          background: '#f9f9f9',
          borderRadius: '4px',
          color: '#666',
        }}>
          No forecast data available
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem',
          }}>
            <div style={{
              background: 'white',
              padding: '1rem',
              borderRadius: '4px',
              border: '1px solid #e0e0e0',
            }}>
              <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                Estimated
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#333' }}>
                {totals.estimated.toFixed(1)}h
              </div>
            </div>

            <div style={{
              background: 'white',
              padding: '1rem',
              borderRadius: '4px',
              border: '1px solid #e0e0e0',
            }}>
              <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                Actual
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#333' }}>
                {totals.actual.toFixed(1)}h
              </div>
            </div>

            <div style={{
              background: 'white',
              padding: '1rem',
              borderRadius: '4px',
              border: `2px solid ${getVarianceColor(totals.variance)}`,
            }}>
              <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                Variance
              </div>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: 600,
                color: getVarianceColor(totals.variance),
              }}>
                {totals.variance > 0 ? '+' : ''}{totals.variance.toFixed(1)}h
              </div>
            </div>
          </div>

          {/* Details Table */}
          <div style={{
            background: 'white',
            borderRadius: '4px',
            border: '1px solid #e0e0e0',
            overflow: 'hidden',
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.9rem',
            }}>
              <thead>
                <tr style={{ background: '#f9f9f9', borderBottom: '1px solid #e0e0e0' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600, color: '#555' }}>
                    Project
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'right', fontWeight: 600, color: '#555' }}>
                    Estimated
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'right', fontWeight: 600, color: '#555' }}>
                    Actual
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'right', fontWeight: 600, color: '#555' }}>
                    Variance
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'right', fontWeight: 600, color: '#555' }}>
                    % Var
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((item) => (
                  <tr
                    key={item.ProjectId}
                    style={{
                      borderBottom: '1px solid #e0e0e0',
                      background: item.Variance !== 0 ? '#f9f9f9' : 'white',
                    }}
                  >
                    <td style={{ padding: '1rem', color: '#333' }}>
                      {item.ProjectTitle}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right', color: '#555' }}>
                      {item.EstimatedHours.toFixed(1)}h
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right', color: '#555' }}>
                      {item.ActualHours.toFixed(1)}h
                    </td>
                    <td style={{
                      padding: '1rem',
                      textAlign: 'right',
                      fontWeight: 600,
                      color: getVarianceColor(item.Variance),
                    }}>
                      {item.Variance > 0 ? '+' : ''}{item.Variance.toFixed(1)}h
                    </td>
                    <td style={{
                      padding: '1rem',
                      textAlign: 'right',
                      fontWeight: 600,
                      color: getVarianceColor(item.VariancePercent, true),
                    }}>
                      {item.VariancePercent > 0 ? '+' : ''}{item.VariancePercent.toFixed(0)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            background: '#f9f9f9',
            borderRadius: '4px',
            fontSize: '0.85rem',
            color: '#666',
          }}>
            <strong>Legend:</strong> Green = under budget | Yellow = slightly over | Red = significantly over budget
          </div>
        </>
      )}
    </div>
  );
}
